<template>
  <div class="booking-page py-4 sm:py-8">
    <div class="container mx-auto px-1 sm:px-4 max-w-4xl">
      <!-- Page Title -->
      <h1 class="text-3xl sm:text-2xl font-bold mb-6 text-center" :style="{ color: currentPrimaryColor }">{{ $t('booking.title') }}</h1>

      <!-- Booking Form -->
      <div class="space-y-4 sm:space-y-6">
        <!-- Step 1: Party Size -->
        <div class="booking-card-bg p-3 sm:p-6 rounded-lg shadow-card">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 sm:h-5 sm:w-5 text-primary mr-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <h3 class="text-2xl sm:text-lg font-semibold dark:text-gray-200">{{ $t('booking.partySize') }}</h3>
            </div>

            <div class="flex items-center">
              <button
                @click="decreasePartySize"
                class="rounded-full w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center border border-gray-300 booking-card-bg text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                :disabled="partySize <= 1"
                :class="{ 'opacity-50 cursor-not-allowed': partySize <= 1 }"
                aria-label="Decrease party size"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
                </svg>
              </button>

              <span class="mx-3 text-3xl sm:text-lg font-medium w-10 sm:w-6 text-center">{{ partySize }}</span>

              <button
                @click="increasePartySize"
                class="rounded-full w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center border border-gray-300 booking-card-bg text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                :disabled="partySize >= maxPartySize"
                :class="{ 'opacity-50 cursor-not-allowed': partySize >= maxPartySize }"
                aria-label="Increase party size"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Step 2: Date List (only shown when data is available) -->
        <transition
          name="fade-slide-up"
          mode="out-in"
          appear
        >
          <div v-if="!initialLoading && bookingStore.availableDates.length > 0" class="booking-card-bg p-3 sm:p-6 rounded-lg shadow-card transform transition duration-500">
            <!-- Date selection summary -->
            <div v-if="selectedDate && isDateSectionCollapsed"
                 class="flex justify-between items-center transition-all duration-300 ease-in-out">
              <div class="flex items-center">
                <div class="mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 sm:h-5 sm:w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 class="text-2xl sm:text-lg font-semibold dark:text-gray-200">{{ formattedSelectedDate }}</h3>
              </div>
              <button
                @click="toggleDateSection(false)"
                class="rounded-full w-8 h-8 sm:w-7 sm:h-7 flex items-center justify-center text-primary hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-150"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            <!-- Full date picker section with Vue's transition component -->
            <transition
              name="collapse-date"
              @enter="onTransitionEnter"
              @leave="onTransitionLeave"
              :css="false"
            >
              <div v-if="!selectedDate || !isDateSectionCollapsed" class="date-expander">
                <!-- Header inside the transition container -->
                <h3 class="text-2xl sm:text-lg font-semibold mb-4 dark:text-gray-200">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 sm:h-5 sm:w-5 text-primary mr-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {{ $t('booking.dateSelection') }}
                  </div>
                </h3>

                <!-- Date list component -->
                <DateList
                  v-model="selectedDate"
                  :availableDates="bookingStore.availableDates"
                  :loading="false"
                  :error="error || ''"
                  @date-click="handleDateClick"
                />
              </div>
            </transition>
          </div>
        </transition>

        <!-- Step 3: Time Selector (only shown after date is selected) -->
        <transition
          name="fade-slide-up"
          mode="out-in"
          appear
        >
          <div v-if="selectedDate" class="booking-card-bg p-3 sm:p-6 rounded-lg shadow-card transform transition duration-500">
            <!-- Time selection summary -->
            <div v-if="selectedTime && isTimeSectionCollapsed"
                class="flex justify-between items-center transition-all duration-300 ease-in-out">
              <div class="flex items-center">
                <div class="mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 sm:h-5 sm:w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 class="text-2xl sm:text-lg font-semibold dark:text-gray-200">{{ formattedTime }}</h3>
              </div>
              <button
                @click="toggleTimeSection(false)"
                class="rounded-full w-8 h-8 sm:w-7 sm:h-7 flex items-center justify-center text-primary hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-150"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            <!-- Full time picker section -->
            <transition
              name="collapse-time"
              @enter="onTransitionEnter"
              @leave="onTransitionLeave"
              :css="false"
            >
              <div v-if="!selectedTime || !isTimeSectionCollapsed" class="time-expander">
                <!-- Header inside the transition container -->
                <h3 class="text-2xl sm:text-lg font-semibold mb-4 dark:text-gray-200">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 sm:h-5 sm:w-5 text-primary mr-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {{ $t('booking.timeSelection') }}
                  </div>
                </h3>

                <!-- Time selector component -->
                <TimeSelector
                  v-model="selectedTime"
                  :availableTimes="availableTimes"
                  :loading="loadingTimes"
                  @time-click="handleTimeClick"
                />
              </div>
            </transition>
          </div>
        </transition>

        <!-- Step 4: Guest Information Form (Contact Section) -->
        <transition
          name="fade-slide-up"
          mode="out-in"
          appear
        >
          <div v-if="selectedDate && selectedTime" class="booking-card-bg p-3 sm:p-6 rounded-lg shadow-card transform transition duration-500">
            <!-- Contact header -->
            <h3 class="text-2xl sm:text-lg font-semibold mb-4 dark:text-gray-200">
              <div class="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 sm:h-5 sm:w-5 text-primary mr-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {{ $t('booking.contactInfo') }}
              </div>
            </h3>

            <!-- Guest info form component -->
            <GuestInfoForm
              ref="guestInfoForm"
              v-model="guestInfo"
              :isLoading="isSubmitting"
              @validated="onFormValidated"
            />
          </div>
        </transition>

        <!-- Step 5: Reservation Details Section -->
        <transition
          name="fade-slide-up"
          mode="out-in"
          appear
        >
          <ReservationDetails
            v-if="selectedDate && selectedTime"
            ref="reservationDetailsComponent"
            v-model:specialRequests="specialRequests"
            v-model:termsAccepted="termsAccepted"
            v-model:isTermsExpanded="isTermsExpanded"
            :formattedDate="formattedDateWithoutYear"
            :formattedTime="formattedTime"
            :partySize="partySize"
            :showTermsError="showTermsError"
            :isSubmitting="isSubmitting"
            :isBookingInProgress="isBookingInProgress"
            :shouldEnableButton="shouldEnableButton"
            @special-request-focus="handleSpecialRequestFocus"
            @submit="validateAndBook"
          />
        </transition>

        <!-- Help text when dates are available but not yet selected -->
        <transition
          name="fade-slide-up"
          mode="out-in"
          appear
        >
          <div v-if="!initialLoading && bookingStore.availableDates.length > 0 && !selectedDate" class="bg-blue-50 p-6 rounded-lg border border-blue-100 transform transition duration-500 dark:bg-blue-900 dark:border-blue-800 dark:text-blue-100">
            <div class="text-blue-700 dark:text-blue-200 mb-2 font-medium">{{ $t('booking.helpTitle') }}</div>
            <p class="text-blue-600 dark:text-blue-300 text-sm">{{ $t('booking.helpText') }}</p>
          </div>
        </transition>

        <!-- Help text when time is available but not yet selected -->
        <transition
          name="fade-slide-up"
          mode="out-in"
          appear
        >
          <div v-if="selectedDate && !selectedTime" class="bg-blue-50 p-6 rounded-lg border border-blue-100 transform transition duration-500 dark:bg-blue-900 dark:border-blue-800 dark:text-blue-100">
            <div class="text-blue-700 dark:text-blue-200 mb-2 font-medium">{{ $te('booking.selectTimeTitle') ? $t('booking.selectTimeTitle') : $t('booking.helpTitle') }}</div>
            <p class="text-blue-600 dark:text-blue-300 text-sm">{{ $te('booking.selectTimeText') ? $t('booking.selectTimeText') : $t('booking.helpText') }}</p>
          </div>
        </transition>

        <!-- Error Message Display -->
        <transition name="fade">
          <div v-if="error" class="p-4 bg-red-50 text-red-700 dark:bg-red-900 dark:text-red-200 rounded-lg transform transition duration-500">
            {{ error }}
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, nextTick, defineAsyncComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import { useBookingStore } from '@/stores/booking'
import { usePlace } from '@/composables/usePlace'
import DateList from '@/components/booking/DateList.vue'
import BrandedHeader from '@/components/booking/BrandedHeader.vue'
import { debounce } from '@/utils/debounceUtils'
// Lazy-load components that aren't needed immediately
// Lazy-load the TimeSelector component
// Our dynamic import fixes in main.ts will ensure this works with the correct base path
const TimeSelector = defineAsyncComponent(() => import('@/components/booking/TimeSelector.vue'))
const GuestInfoForm = defineAsyncComponent(() => import('@/components/booking/GuestInfoForm.vue'))
const ReservationDetails = defineAsyncComponent(() => import('@/components/booking/ReservationDetails.vue'))
import { useBookingFlow } from '@/composables/useBookingFlow'
import { useBookingUI } from '@/composables/useBookingUI'
const { t } = useI18n()
const bookingStore = useBookingStore()
const { currentPrimaryColor } = usePlace()

// Create a template ref for the ReservationDetails component
const reservationDetailsComponent = ref<any>(null)
// Create a template ref for the terms checkbox
const $termsCheckbox = ref<HTMLInputElement | null>(null)

// Create a ref to hold the DOM element we want to scroll to
const scrollTargetRef = ref<HTMLElement | null>(null)

// Use extracted composables
const {
  selectedDate,
  selectedTime,
  partySize,
  availableTimes,
  loadingTimes,
  isSubmitting,
  isBookingInProgress,
  lastSubmitTime,
  error,
  formValidated,
  initialLoading,
  termsAccepted,
  specialRequests,
  guestInfo,
  formattedTime,
  formattedSelectedDate,
  formattedDateWithoutYear,
  shouldEnableButton,
  maxPartySize,
  increasePartySize,
  decreasePartySize,
  createBooking
} = useBookingFlow()

const {
  isDateSectionCollapsed,
  isTimeSectionCollapsed,
  isTermsExpanded,
  showTermsError,
  guestInfoForm,
  toggleDateSection,
  toggleTimeSection,
  onTransitionEnter,
  onTransitionLeave,
  handleFormValidation,
  handleSpecialRequestFocus
} = useBookingUI({
  externalReservationDetailsRef: scrollTargetRef,
  externalTermsCheckboxRef: $termsCheckbox
})

// Add a reactive watch to auto-expand terms section when guest info is valid
// This is more reliable than trying to focus the checkbox
watch([
  () => guestInfo.value?.name,
  () => guestInfo.value?.mobile?.number,
  () => guestInfo.value?.mobile?.countryCode
], () => {
  // Check if we have valid guest info
  const hasName = guestInfo.value?.name && guestInfo.value.name.trim().length >= 2;
  const hasPhone = guestInfo.value?.mobile?.number &&
                   guestInfo.value.mobile.number.trim().length > 0 &&
                   guestInfo.value?.mobile?.countryCode;

  // If both name and phone are filled, auto-expand terms section
  if (hasName && hasPhone) {
    // Auto-expand terms section immediately - this doesn't require typing to have stopped
    isTermsExpanded.value = true;

    // Store the current values to check if they change
    const currentName = guestInfo.value?.name;
    const currentPhoneNumber = guestInfo.value?.mobile?.number;

    // Add a moderate delay to allow UI to settle and to check if user is still typing
    setTimeout(() => {
      // Only proceed with scrolling if values haven't changed and form is validated
      if (currentName === guestInfo.value?.name &&
          currentPhoneNumber === guestInfo.value?.mobile?.number) {

        // Only auto-scroll if the form has been explicitly validated
        if (formValidated.value) {
          // Scroll to reservation details smoothly
          if (reservationDetailsComponent.value) {
            const el = reservationDetailsComponent.value.$el || reservationDetailsComponent.value;
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } else {
          // If the form looks valid but formValidated isn't set,
          // we may need to trigger validation explicitly to ensure proper state
          if (guestInfoForm.value && hasName && hasPhone) {
            // Trigger form validation to update formValidated state
            guestInfoForm.value.validate();
          }
        }
      } else {
        // Values changed during delay, user might still be typing
      }
    }, 600); // Moderate delay for better detection of completed typing
  }
}, { immediate: true });

// Helper function to find the terms checkbox in the ReservationDetails component
const findTermsCheckbox = (parentElement: HTMLElement): HTMLInputElement | null => {
  // Try to find it by a specific class first
  const checkbox = parentElement.querySelector('input[type="checkbox"].terms-checkbox')
  if (checkbox) {
    return checkbox as HTMLInputElement
  }

  // If not found by class, try any checkbox
  const anyCheckbox = parentElement.querySelector('input[type="checkbox"]')
  if (anyCheckbox) {
    return anyCheckbox as HTMLInputElement
  }

  // Could not find any checkbox
  return null
}

// Function to ensure terms section is expanded and focus on checkbox
const expandTermsAndFocusCheckbox = () => {
  // Make sure terms are expanded
  if (!isTermsExpanded.value) {
    isTermsExpanded.value = true
  }

  // Focus the checkbox after a short delay to allow expansion animation to complete
  setTimeout(() => {
    if ($termsCheckbox.value) {
      // Focus the terms checkbox to guide user attention to required action
      $termsCheckbox.value.focus()
    }
    // If checkbox not found, user can still manually interact with the form
  }, 300)
}

// Custom event handler for form validation
const onFormValidated = (isValid: boolean) => {
  // Strategic debugging point for form validation flow
  // debugger; // Removed in production builds

  const guestInfoValue = guestInfo.value || { name: '', mobile: '' }

  // Add a double-check to ensure we only process valid form submissions
  // This prevents auto-scrolling on invalid mobile numbers or incomplete forms
  if (!isValid) {
    // Mark form as invalid and exit early to prevent further processing
    formValidated.value = false
    return
  }

  // Update validation state immediately to allow other watchers to respond
  formValidated.value = isValid

  // If we've already validated this form and scrolled, don't do it again
  // unless validation state has changed (e.g. from invalid to valid)
  if (isValid === true) {
    // Make sure we have access to both refs before calling handleFormValidation
    // This ensures the DOM is ready
    nextTick(() => {
      // First check if we have the reservation details element
      if (reservationDetailsComponent.value) {
        // Get the actual DOM element from the component
        const el = reservationDetailsComponent.value.$el || reservationDetailsComponent.value
        scrollTargetRef.value = el

        // Find the checkbox on validation and prepare for focus
        // Create a more reliable approach with multiple attempts to handle async component rendering
        if (!$termsCheckbox.value) {
          let attempts = 0
          const findAndFocusCheckbox = () => {
            // Strategic debugging point for checkbox discovery logic
            // debugger; // Removed in production builds

            // Try to get the checkbox directly from the component instance first (preferred method)
            if (reservationDetailsComponent.value && reservationDetailsComponent.value.termsCheckboxRef) {
              // Direct component reference found - use it immediately
              $termsCheckbox.value = reservationDetailsComponent.value.termsCheckboxRef
              expandTermsAndFocusCheckbox()
              return
            }

            // Fallback to DOM search if component reference not available
            $termsCheckbox.value = findTermsCheckbox(el)
            if ($termsCheckbox.value) {
              // Checkbox found via DOM search - proceed with focus
              expandTermsAndFocusCheckbox()
            } else if (attempts < 3) {
              // Checkbox not found yet - retry up to 3 times to handle async rendering
              attempts++
              setTimeout(findAndFocusCheckbox, 150)
            }
          }

          // Start the find and focus process
          setTimeout(findAndFocusCheckbox, 150)
        } else {
          // If checkbox ref already exists, focus it after a delay
          expandTermsAndFocusCheckbox()
        }
      }

      // Now call the handler with both refs properly set up
      // This will perform the actual scrolling
      handleFormValidation(isValid, guestInfoValue)
    })
  }
}

// Watch for changes in form fields and clear errors
watch([
  () => guestInfo.value?.name,
  () => guestInfo.value?.mobile,
  termsAccepted,
  selectedDate,
  selectedTime
], () => {
  // Clear error when user makes changes
  if (error.value && shouldEnableButton.value) {
    error.value = ''
  }
}, { immediate: true })

// Fetch available times when date changes
watch(selectedDate, async (newDate, oldDate) => {
  if (newDate) {
    // Auto-collapse the date section when a date is selected
    // Faster delay to match the quicker animations
    setTimeout(() => {
      toggleDateSection(true)
    }, 100)

    // Update the selected date in the store
    bookingStore.selectDate(newDate)

    // If the oldDate is the same as newDate, don't reset time selection
    if (oldDate && oldDate.toDateString() === newDate.toDateString()) {
      return
    }

    loadingTimes.value = true
    selectedTime.value = null
    availableTimes.value = [] // Initialize with empty array before the async call

    try {
      // First check if we have times in the store
      const selectedDateInfo = bookingStore.availableDates.find(d => {
        const date = new Date(d.date)
        const selected = new Date(newDate)
        return date.toDateString() === selected.toDateString()
      })

      if (selectedDateInfo && selectedDateInfo.availableTimes.length > 0) {
        availableTimes.value = selectedDateInfo.availableTimes
      } else {
        // Fallback to the direct API call
        loadingTimes.value = true
        availableTimes.value = await bookingStore.getAvailableTimesForDate(newDate)
      }
    } catch (err) {
      error.value = t('common.error')
      console.error('Error fetching availability data:', err instanceof Error ? err.message : 'Unknown error')
      availableTimes.value = [] // Ensure it's an empty array in case of error
    } finally {
      loadingTimes.value = false
    }
  } else {
    // If date is deselected, make sure section is expanded
    toggleDateSection(false)

    // Clear the selected date in the store
    bookingStore.selectDate(null as unknown as Date)

    // Clear the time selection
    selectedTime.value = null
    availableTimes.value = []
  }
})

// Watch for time selection change and auto-collapse the section
watch(selectedTime, (newTime) => {
  if (newTime) {
    // Auto-collapse the time section when a time is selected
    // Faster delay to match the quicker animations
    setTimeout(() => {
      toggleTimeSection(true)

      // Focus the guest info form when a time is selected
      // Adjust the delay to ensure proper rendering and focus
      setTimeout(() => {
        if (guestInfoForm.value) {
          guestInfoForm.value.focusFirstInput()
        }
      }, 400) // Longer single delay for smoother transition
    }, 100)
  } else {
    // If time is deselected, make sure section is expanded
    toggleTimeSection(false)
  }
})

// Validate form and create booking if valid
const validateAndBook = async () => {
  // Strategic debugging point for booking validation flow
  // debugger; // Removed in production builds

  // Debounce mechanism - prevent multiple rapid taps
  const now = Date.now()
  if (now - lastSubmitTime.value < 1000) { // 1000ms debounce
    return
  }
  lastSubmitTime.value = now

  // If already submitting, don't allow another submission
  if (isSubmitting.value || isBookingInProgress.value) {
    return
  }

  // Clear any previous errors
  error.value = ''
  showTermsError.value = false

  if (!selectedDate.value || !selectedTime.value) {
    error.value = t('booking.missingDateOrTime')
    return
  }

  if (!guestInfo.value?.name || !guestInfo.value?.mobile?.number) {
    error.value = t('booking.missingRequiredFields')
    return
  }

  if (!termsAccepted.value) {
    showTermsError.value = true
    error.value = t('booking.required.terms')
    // Auto-expand terms section
    isTermsExpanded.value = true
    return
  }

  if (guestInfoForm.value) {
    // This will trigger the form validation which will emit the validated event
    const isValid = guestInfoForm.value.validate()

    // If the validate method returns a value directly (might be synchronous)
    if (typeof isValid === 'boolean') {
      if (isValid) {
        await createBooking()
      }
    }
    // Otherwise, the validate method will emit the 'validated' event which is handled by handleFormValidation
  } else {
    console.error('GuestInfoForm ref is not available')
    error.value = t('common.error')
  }
}

const handleDateClick = (date: Date) => {
  // If this is the same date that's already selected, just collapse the date section
  // without resetting the time selection
  if (selectedDate.value && date.toDateString() === selectedDate.value.toDateString()) {
    toggleDateSection(true)

    // Prevent the watch handler from running full flow for the same date
    return
  }
}

const handleTimeClick = (time: string) => {
  // If this is the same time that's already selected, collapse the time section
  if (time === selectedTime.value) {
    toggleTimeSection(true)
  }
}

// Initialize data and state on component mount
onMounted(async () => {
  initialLoading.value = true

  try {
    // Restore values from store if they exist (e.g., coming back from another page)
    if (bookingStore.selectedDate) {
      selectedDate.value = bookingStore.selectedDate
    }

    if (bookingStore.selectedTime) {
      selectedTime.value = bookingStore.selectedTime
    }

    if (bookingStore.partySize) {
      partySize.value = bookingStore.partySize
    }

    if (bookingStore.guestInfo) {
      guestInfo.value = { ...bookingStore.guestInfo }
    }

    // Add a short delay to prevent UI jank during initial rendering
    await new Promise(resolve => setTimeout(resolve, 100))

    // Fetch availability data once (no need to clear cache on initial load)
    await bookingStore.fetchAllAvailabilityData()

    // Update available dates for the current party size
    await bookingStore.updateAvailableDatesForPartySize(partySize.value)

  } catch (err) {
    error.value = t('common.error')
    console.error('Error during initialization:', err instanceof Error ? err.message : 'Unknown error')
  } finally {
    initialLoading.value = false
  }
})

// Create a debounced function for handling party size changes
// This prevents multiple rapid API calls when party size changes
const debouncedHandlePartySizeChange = debounce((newSize: number, oldSize: number) => {
  if (newSize !== oldSize) {
    // Update the store with the new party size without forcing cache clear
    bookingStore.updateAvailableDatesForPartySize(newSize)
  }
}, 500) // 500ms debounce delay

// Watch for direct changes to partySize outside of the increase/decrease methods
watch(partySize, (newSize, oldSize) => {
  if (newSize !== oldSize) {
    // Use the debounced handler to prevent multiple rapid API calls
    debouncedHandlePartySizeChange(newSize, oldSize)
  }
})
</script>

<style scoped>
.booking-page {
  animation: pageIn 0.5s ease-out;
}

@keyframes pageIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* All transition styles are now handled in JavaScript for greater reliability */
.date-expander,
.time-expander {
  transform-origin: top center;
  will-change: height, opacity, transform;
}

/* For smoother animation delay */
.animation-delay-50 { animation-delay: 50ms; }
.animation-delay-100 { animation-delay: 100ms; }
.animation-delay-150 { animation-delay: 150ms; }

/* Hide the outline on focus for buttons but keep accessibility */
button:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.5);
}

.form-container {
  isolation: isolate;
  z-index: 1000;
  position: relative;
}

/* Define the fade-slide-up transition for progressive disclosure */
.fade-slide-up-enter-active,
.fade-slide-up-leave-active {
  transition: all 0.4s ease;
}

.fade-slide-up-enter-from,
.fade-slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.fade-slide-up-enter-to,
.fade-slide-up-leave-from {
  opacity: 1;
  transform: translateY(0);
}

/* Simple fade transition for error message */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>